import { useCallback } from "react";
import {
  AlertSeverityEnum,
  OrdenTrabajo,
} from "@/shared/interfaces";
import { useMaterialesUtilizadosStore } from "@/store/erp";
import { EquiposUtilizadosOTTableType } from "../../equipos";
import {
  validarOrdenTrabajo,
} from "../utils/validarOrdenTrabajo";
import {
  calcularHistorialCambiosCoordenadas,
  construirPayloadOrdenTrabajo,
} from "../utils/construirPayloadOrdenTrabajo";
import { ordenTrabajoSubeTecnicoFormSchema, useRecordDirectLock } from "@/shared";
import { toast } from "react-toastify";
import { UseFormReturn } from "react-hook-form";

interface useSaveOrdenTrabajoParams {
  form: UseFormReturn<any>;
  ordentrabajo: OrdenTrabajo;
  isValid: boolean;
  isCorrentingData: boolean;
  currentUser: { username?: string } | null;
  handleAgregarSeriesArrayProductos: (materiales: any[]) => any[];
  MATERIALES_UTILIZADOS: any[];
  productos_adicionales: any[];
  updateOrdenTrabajoMutation: { mutate: (params: { id: string | number; data: any }) => void };
  updateContratoMutation: { mutate: (params: { id: string | number; data: any }) => void };
  clearAllMaterials: () => void;
  refreshData: () => Promise<void>;
}

export function useSaveOrdenTrabajo({
  form,
  ordentrabajo,
  isValid,
  isCorrentingData,
  currentUser,
  handleAgregarSeriesArrayProductos,
  MATERIALES_UTILIZADOS,
  productos_adicionales,
  updateOrdenTrabajoMutation,
  updateContratoMutation,
  clearAllMaterials,
  refreshData,
}: useSaveOrdenTrabajoParams) {
  const { releaseLock } = useRecordDirectLock(ordentrabajo.id_orden_trabajo);
  const onSave = useCallback(async (data: any) => {
    const esBorrador = !!data.es_borrador;

    if (!isValid && !esBorrador) return;

    const equiposUtilizados: EquiposUtilizadosOTTableType[] =
      useMaterialesUtilizadosStore.getState().equipos_utilizados;
    const materialesFiltrados = handleAgregarSeriesArrayProductos(MATERIALES_UTILIZADOS);

    const resultado = validarOrdenTrabajo({
      data,
      esBorrador,
      ordentrabajo: ordentrabajo as any,
      equiposUtilizados,
      materialesFiltrados,
      productosAdicionales: productos_adicionales,
    });

    if (!resultado.valido) {
      return toast.error(resultado.mensaje);
    }

    const historial_cambios_coordenadas = calcularHistorialCambiosCoordenadas({
      ordentrabajo,
      coordenadasNuevas: data?.coordenadas,
      usuario: currentUser?.username,
    });

    const payload = construirPayloadOrdenTrabajo({
      data: { ...data, historial_cambios_coordenadas },
      ordentrabajo,
      materialesFiltrados,
      productosAdicionales: productos_adicionales,
      isCorrentingData,
      esBorrador,
    });

    updateOrdenTrabajoMutation.mutate({
      id: ordentrabajo?.id_orden_trabajo!,
      data: payload,
    });


    const wifiValido = await validarYPintarCamposWifi(data, form);
    if (!wifiValido) {
      return toast.error("Revisa los campos de WiFi (SSID / contraseña)");
    }
    if ((data.ssid && ordentrabajo?.contrato_data?.ssid !== data.ssid) 
      || (data.password_wifi && ordentrabajo?.contrato_data?.password_wifi !== data.password_wifi)) {
      updateContratoMutation.mutate({
        id: ordentrabajo?.contrato_data?.id_contrato!,
        data: {
          ssid: data.ssid?.trim(),
          password_wifi: data.password_wifi?.trim(),
        },
      });
    }

    if (!esBorrador) {
      await releaseLock();
      await refreshData();
    }

    clearAllMaterials();
  }, [
    isValid,
    ordentrabajo,
    handleAgregarSeriesArrayProductos,
    MATERIALES_UTILIZADOS,
    productos_adicionales,
    currentUser,
    isCorrentingData,
    updateOrdenTrabajoMutation,
    clearAllMaterials,
    releaseLock,
    refreshData,
  ]);

  return { onSave };
}

const WIFI_FIELDS = ["ssid", "password_wifi"] as const;
async function validarYPintarCamposWifi(
  data: any,
  form: UseFormReturn<any>,
): Promise<boolean> {
  let hayErrores = false;

  for (const field of WIFI_FIELDS) {
    try {
      await ordenTrabajoSubeTecnicoFormSchema
        .pick([field])
        .validate(data, { abortEarly: false });
      form.clearErrors(field); // ✅ el campo está bien, se limpia cualquier error previo
    } catch (err: any) {
      const mensaje = err.inner?.[0]?.message ?? err.message;
      form.setError(field, { type: "manual", message: mensaje }); // ❌ se pinta en rojo
      hayErrores = true;
    }
  }

  return !hayErrores;
}